// API configuration and utilities for Cleancy Web App

// Default API URL - can be overridden with environment variable
// When deployed on Heroku, it will use the same domain with /api path
const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production"
    ? `${window.location.protocol}//${window.location.host}/api`
    : "http://localhost:3001");

// Generic error handler for API responses
export const getErrorMessage = (error: any): string => {
  if (error.response) {
    // Server responded with error status
    return (
      error.response.data?.error ||
      error.response.data?.message ||
      error.response.statusText ||
      "Server error"
    );
  } else if (error.request) {
    // Request was made but no response received
    return "No response from server";
  } else {
    // Something else happened
    return error.message || "Unknown error";
  }
};

export class ApiError extends Error {
  readonly status: number;
  readonly retryAfter?: number;
  readonly code?: string;

  constructor(message: string, status: number, retryAfter?: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfter = retryAfter;
    this.code = code;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

const parseRetryAfterSeconds = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return Math.ceil(value);
  }

  if (typeof value !== "string" || !value.trim()) return undefined;

  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && numericValue >= 0) {
    return Math.ceil(numericValue);
  }

  // Retry-After may also be an HTTP date.
  const retryAt = Date.parse(value);
  if (Number.isNaN(retryAt)) return undefined;
  return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
};

const createApiError = async (response: Response): Promise<ApiError> => {
  const errorData: any = await response.json().catch(() => ({}));
  const retryAfterHeader = response.headers?.get?.("Retry-After");
  const retryAfter =
    parseRetryAfterSeconds(errorData?.retryAfter) ??
    parseRetryAfterSeconds(retryAfterHeader);
  const message =
    (typeof errorData?.error === "string" && errorData.error) ||
    (typeof errorData?.message === "string" && errorData.message) ||
    `HTTP ${response.status}: ${response.statusText}`;

  const code =
    typeof errorData?.code === "string" && errorData.code
      ? errorData.code
      : undefined;

  return new ApiError(message, response.status, retryAfter, code);
};

// Fetch request helper with error handling
export const fetchWithErrorHandling = async (
  url: string,
  options?: RequestInit
) => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });

    if (!response.ok) {
      // Surface the server's error message instead of an opaque "HTTP 401:".
      // The errorHandler returns { error } (some routes { message }).
      throw await createApiError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    if (error instanceof ApiError) throw error;
    throw new Error(getErrorMessage(error));
  }
};

// Fetch request helper with authentication
export const fetchWithAuth = async (
  url: string,
  options?: RequestInit
) => {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    throw new Error('Not authenticated');
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
        ...options?.headers,
      },
    });

    if (!response.ok) {
      throw await createApiError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

/**
 * Small (~256px) thumbnail variant of a stored image, generated and cached
 * server-side on first request. Use for small surfaces (list rows); full views
 * keep using the original image URL. Already-absolute URLs are returned as-is.
 */
export const thumbUrl = (fileName: string): string => {
  if (!fileName) {
    return fileName;
  }
  if (/^https?:\/\//i.test(fileName) || fileName.startsWith("data:")) {
    return fileName;
  }
  // The thumbnail endpoint takes a bare object key (no leading slash).
  const key = fileName.replace(/^\/+/, "");
  return `${API_URL}/images/thumb/${encodeURIComponent(key)}`;
};

export { API_URL };
